import React from 'react'
import { Loader2 } from 'lucide-react'

export type BatchStatus =
  | 'created'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'stopped'

interface StatusBadgeProps {
  status: BatchStatus
  queuePosition?: number | null
  /** When status is completed, show "Partially completed" if progress < 100 or any failed. */
  progress?: number
  failedCount?: number
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  queuePosition,
  progress,
  failedCount,
}) => {
  let classes =
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border'
  let label: string = status

  const isPartiallyCompleted =
    status === 'completed' &&
    ((progress != null && progress < 100) || (failedCount ?? 0) > 0)

  switch (status) {
    case 'created':
      classes += ' bg-gray-100 text-gray-700 border-gray-200'
      label = 'Created'
      break
    case 'queued':
      classes += ' bg-blue-100 text-blue-700 border-blue-200'
      label =
        queuePosition != null && queuePosition > 0
          ? `Queued (#${queuePosition})`
          : 'Queued'
      break
    case 'processing':
      classes += ' bg-purple-100 text-purple-700 border-purple-200'
      label = 'Processing'
      break
    case 'completed':
      if (isPartiallyCompleted) {
        classes += ' bg-amber-100 text-amber-700 border-amber-200'
        label = 'Partially completed'
      } else {
        classes += ' bg-green-100 text-green-700 border-green-200'
        label = 'Completed'
      }
      break
    case 'failed':
      classes += ' bg-red-100 text-red-700 border-red-200'
      label = 'Failed'
      break
    case 'stopped':
      classes += ' bg-amber-100 text-amber-700 border-amber-200'
      label = 'Stopped'
      break
    default:
      break
  }

  return (
    <span className={classes}>
      {status === 'processing' && (
        <Loader2 className='w-3 h-3 animate-spin' aria-hidden='true' />
      )}
      <span>{label}</span>
    </span>
  )
}

export default StatusBadge
